import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoggingModule, LoggingService } from '@services/logging/logging.module';
import { ModalFormHostModule } from '@shared/components/modal-form-host/modal-form-host.module';
import { ModelFormHostExModule } from '@shared/components/model-form-host-ex/model-form-host-ex.module';
import { InvitedeskApiModule } from '@shared/services/api/legacy/invitedesk-api.module';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxSelectModule } from 'ngx-select-ex';
import { CalendarModule } from 'primeng/calendar';
import { ColorsService } from './colors/colors.service';
import { EditRegistrationFormComponent } from './components/edit-registration-form/edit-registration-form.component';
import { AttendeePagingService } from './services/event-paging-service/attendee-paging.service';
import { InvitationPagingService } from './services/event-paging-service/invitation-paging.service';
import { EventPagingService } from './services/event-paging-service/event-paging.service';
import { TranslationService } from './services/translation/translation.service';
import { ChartService } from './services/chart/chart.service';
import { HardcodedTranslationService } from './services/translation/hardcoded-translation.service';
import { LocaleService } from './services/locale/locale.service';
import { SchedulingApiModule } from './services/api/scheduling/scheduling-api.module';
import { TimezoneService } from './services/timezone/timezone.service';
import { FroalaService } from './services/froala/froala.service';
import { CampaignPagingService } from './services/campaign-paging-service/campaign-paging.service';
import { RecipientPagingService } from './services/campaign-paging-service/recipient-paging.service';
import { MailjetStatusesService } from './services/mailjet-statuses/mailjet-statuses.service';
import { DashboardApiModule } from './services/api/dashboard/dashboard-api.module';
import { PdfService } from './services/pdf/pdf.service';
import { State } from './services/store/state.service';
import { NgxUploaderModule } from 'ngx-uploader';
import { MailStatusPagingService } from './services/paging/mailstatus-paging.service';
import { EditContactFormComponent } from './components/edit-contact-form/edit-contact-form.component';
import { TagInputModule } from 'ngx-chips';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { MomentModule } from 'ngx-moment';
import { UtmPagingService } from './services/event-paging-service/utm-paging.service';
import { CustomPipesModule } from './pipes/custom-pipes.module';
import { MultiSelectModule } from 'primeng/multiselect';
import { MetaService } from './services/meta/meta.service';
import { StarRatingComponent } from './components/star-rating/star-rating.component';
import { FooterViewComponent } from './components/footer-view/footer-view.component';
import { AdvancedTemplateViewComponent } from './components/advanced-template-view/advanced-template-view.component';
import { AdvancedTemplatePreviewComponent } from './components/advanced-template-preview/advanced-template-preview.component';
import { PromotionPagingService } from './services/event-paging-service/promotion-paging.service';
import { PaginationComponent } from './components/pagination/pagination.component';
import { AdvancedTemplatesFilterComponent } from './components/advanced-templates-filter/advanced-templates-filter.component';
import { TrimOnBlurDirective } from './directives/trim.directive';
import { CloseOnOutsideClickDirective } from './directives/click-outside.directive';

// https://angular.io/styleguide#!#04-10
@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        AccordionModule.forRoot(),
        AlertModule.forRoot(),
        ButtonsModule.forRoot(),
        CarouselModule.forRoot(),
        CollapseModule.forRoot(),
        BsDatepickerModule.forRoot(),
        BsDropdownModule.forRoot(),
        ModalModule.forRoot(),
        PaginationModule.forRoot(),
        ProgressbarModule.forRoot(),
        RatingModule.forRoot(),
        TabsModule.forRoot(),
        TimepickerModule.forRoot(),
        TooltipModule.forRoot(),
        PopoverModule.forRoot(),
        TypeaheadModule.forRoot(),
        LoggingModule,
        NgxSelectModule,
        CalendarModule,
        InvitedeskApiModule,
        SchedulingApiModule,
        DashboardApiModule,
        ModalFormHostModule,
        ModelFormHostExModule,
        DigitOnlyModule,
        NgxUploaderModule,
        TagInputModule,
        NgxDatatableModule,
        MomentModule,
        CustomPipesModule,
        MultiSelectModule
    ],
    providers: [
        ColorsService,
        EventPagingService,
        AttendeePagingService,
        PromotionPagingService,
        InvitationPagingService,
        CampaignPagingService,
        RecipientPagingService,
        TranslationService,
        ChartService,
        HardcodedTranslationService,
        LocaleService,
        TimezoneService,
        FroalaService,
        MailjetStatusesService,
        PdfService,
        State,
        MailStatusPagingService,
        UtmPagingService,
        MetaService
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        RouterModule,
        AccordionModule,
        AlertModule,
        ButtonsModule,
        CarouselModule,
        CollapseModule,
        BsDatepickerModule,
        BsDropdownModule,
        ModalModule,
        PaginationModule,
        ProgressbarModule,
        RatingModule,
        TabsModule,
        TimepickerModule,
        TooltipModule,
        PopoverModule,
        TypeaheadModule,
        ModalFormHostModule,
        ModelFormHostExModule,
        DigitOnlyModule,
        EditRegistrationFormComponent,
        NgxUploaderModule,
        EditContactFormComponent,
        StarRatingComponent,
        FooterViewComponent,
        AdvancedTemplateViewComponent,
        AdvancedTemplatePreviewComponent,
        PaginationComponent,
        AdvancedTemplatesFilterComponent,
        TrimOnBlurDirective,
        CloseOnOutsideClickDirective,
    ],
    declarations: [
        EditRegistrationFormComponent,
        EditContactFormComponent,
        StarRatingComponent,
        FooterViewComponent,
        AdvancedTemplateViewComponent,
        AdvancedTemplatePreviewComponent,
        PaginationComponent,
        AdvancedTemplatesFilterComponent,
        TrimOnBlurDirective,
        CloseOnOutsideClickDirective,
    ]
})

// https://github.com/ocombe/ng2-translate/issues/209
export class SharedModule {
    constructor(logging: LoggingService) {
        logging.log.info('SharedModule', 'done');
    }

    public static forRoot(): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule
        };
    }
}
